import React, { useState, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { debounce } from 'lodash';
import uniqid from 'uniqid';
import { Grid } from '@mui/material';
import { InputField, SelectSearchField, DateRangeField, CreatableSelectField } from 'vetrf-ui';
import { AutocompleteValue } from 'vetrf-ui/dist/components/form/fields/field/input/AutocompleteField/AutocompleteField';
import { useUserDataContext } from 'components/UserDataContext/context';
import { useFiltersContext } from 'components/FiltersContext/context';
import { CountrySelect } from 'components/CountrySelect';
import { dictApi } from 'api/dict';
import { cerberusIntegrationStatusOptions, yesNoOptions } from 'api/dict/consts';
import {
  AnimalSpeciesDictOption,
  CountryDictOption,
  FeedAdditiveDictOption,
  StatusesDictOption,
} from 'api/dict/params';
import { catalogsApi } from 'modules/catalogs/api/catalogsApi';
import { CatalogName } from 'modules/catalogs/types/api';
import { ROLES } from 'utils/consts/roles';
import { useGetPermission } from 'utils/hooks/useGetPermission';
import { defaultParams } from '../../../pages/FeedRegistry/consts';
import { producerVendorOptions } from '../consts';

export const RegistryFiltersFields = () => {
  const [registryStatusOptions, setRegistryStatusOptions] = useState<StatusesDictOption[]>([]);
  const [countryOptions, setCountryOptions] = useState<CountryDictOption[]>(producerVendorOptions);
  const [additiveTypesOptions, setAdditiveTypesOptions] = useState<FeedAdditiveDictOption[]>([]);
  const [additiveKindOptions, setAdditiveKindsOptions] = useState<FeedAdditiveDictOption[]>([]);
  const [animalTypesOptions, setAnimalTypesOptions] = useState<AnimalSpeciesDictOption[]>([]);
  const [nameOptions, setNameOptions] = useState<{ name: string }[]>([]);
  const [nameLoading, setNameLoading] = useState<boolean>(false);
  const [vendorOptions, setVendorOptions] = useState<{ name: string }[]>([]);
  const [vendorLoading, setVendorLoading] = useState<boolean>(false);
  const [producerOptions, setProducerOptions] = useState<{ name: string }[]>([]);
  const [producerLoading, setProducerLoading] = useState<boolean>(false);
  const { setValue } = useFormContext();
  const { filters } = useFiltersContext();

  const getRegistryStatusOptions = async () => {
    const res = await dictApi.getRegistryStatus();
    setRegistryStatusOptions(res);
  };

  const getCountryOptions = async () => {
    const res = await dictApi.getCountries();
    setCountryOptions((prev) => [...prev, ...res]);
  };

  const getFeedAdditiveTypes = async () => {
    const res = await catalogsApi.findAll<FeedAdditiveDictOption>(CatalogName.FEED_ADDITIVE_TYPES);
    setAdditiveTypesOptions(res);
  };

  const getFeedAdditiveKinds = async () => {
    const res = await catalogsApi.findAll<FeedAdditiveDictOption>(CatalogName.FEED_ADDITIVE_KINDS);
    setAdditiveKindsOptions(res);
  };

  const getAnimalTypes = async () => {
    const res = await dictApi.getAnimalTypes();
    setAnimalTypesOptions(res);
  };

  const { userData } = useUserDataContext();
  const hasCerberusPermission = useGetPermission(ROLES.cerberus, userData);

  useEffect(() => {
    getRegistryStatusOptions();
    if (filters.feedRegistry) {
      Object.entries(filters.feedRegistry).forEach(([key, val]) => {
        setValue(key, val);
      });
    }
  }, []);

  useEffect(() => {
    if (userData) {
      getCountryOptions();
      getFeedAdditiveTypes();
      getAnimalTypes();
      getFeedAdditiveKinds();
    }
  }, [userData]);

  const getNameOptions = async (value: AutocompleteValue) => {
    setNameLoading(true);
    const res = await dictApi.getProductNameAutocompleteList({
      ...defaultParams,
      extendedQuery: { name: Object.values(value).join('') },
    });
    setNameOptions(res.map((el) => ({ name: el })));
    setNameLoading(false);
  };

  const getVendorOptions = async (value: AutocompleteValue) => {
    setVendorLoading(true);
    const res = await dictApi.getVendorAutocompleteList({
      ...defaultParams,
      extendedQuery: { vendor: Object.values(value).join('') },
    });
    setVendorOptions(res.map((el) => ({ name: el })));
    setVendorLoading(false);
  };

  const getProducerOptions = async (value: AutocompleteValue) => {
    setProducerLoading(true);
    const res = await dictApi.getProducerAutocompleteList({
      ...defaultParams,
      extendedQuery: { producer: Object.values(value).join('') },
    });
    setProducerOptions(res.map((el) => ({ name: el })));
    setProducerLoading(false);
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6} xl={4}>
        <CreatableSelectField
          minLength={1}
          clearable
          isLoading={nameLoading}
          fieldClassName="col-xs-12 selectpicker"
          labelClassName="control-label text-nowrap col-xs-12"
          onAutocomplete={debounce(getNameOptions, 500)}
          resetAutocompleteOptions={() => setNameOptions([])}
          labelText="Торговое наименование"
          placeholder=""
          options={nameOptions}
          labelKey="name"
          valueKey="name"
          name="name"
          generateKeyForNewOption={uniqid}
          messageForAddValue="Выбрать"
        />
      </Grid>
      <Grid item xs={12} md={6} xl={4} container>
        <Grid item xs={6}>
          <CreatableSelectField
            minLength={1}
            clearable
            isLoading={vendorLoading}
            fieldClassName="col-xs-12 selectpicker"
            labelClassName="control-label text-nowrap col-xs-12"
            onAutocomplete={debounce(getVendorOptions, 500)}
            resetAutocompleteOptions={() => setVendorOptions([])}
            labelText="Разработчик"
            placeholder=""
            options={vendorOptions}
            labelKey="name"
            valueKey="name"
            name="vendor"
            generateKeyForNewOption={uniqid}
            messageForAddValue="Выбрать"
          />
        </Grid>
        <Grid item xs={6}>
          <CountrySelect fieldName="vendorCountryList" countryOptions={countryOptions} multiple />
        </Grid>
      </Grid>
      <Grid item xs={12} md={6} xl={4} container>
        <Grid item xs={6}>
          <CreatableSelectField
            minLength={1}
            clearable
            isLoading={producerLoading}
            fieldClassName="col-xs-12 selectpicker"
            labelClassName="control-label text-nowrap col-xs-12"
            onAutocomplete={debounce(getProducerOptions, 500)}
            resetAutocompleteOptions={() => setProducerOptions([])}
            labelText="Производитель"
            placeholder=""
            options={producerOptions}
            labelKey="name"
            valueKey="name"
            name="producer"
            generateKeyForNewOption={uniqid}
            messageForAddValue="Выбрать"
          />
        </Grid>
        <Grid item xs={6}>
          <CountrySelect fieldName="producerCountryList" countryOptions={countryOptions} multiple />
        </Grid>
      </Grid>
      {userData && (
        <>
          <Grid item xs={12} md={6} xl={4}>
            <CountrySelect
              labelText="Страна площадки производства"
              fieldName="producerLocationCountryList"
              countryOptions={countryOptions}
              multiple
            />
          </Grid>
          <Grid item xs={12} md={6} xl={4}>
            <SelectSearchField
              clearable
              fieldClassName="col-xs-12 selectpicker"
              labelClassName="col-xs-12 control-label"
              labelKey="label"
              valueKey="value"
              labelText="Наличие инструкции"
              name="haveInstruction"
              placeholder=""
              options={yesNoOptions}
            />
          </Grid>
        </>
      )}
      <Grid item xs={12} md={6} xl={4}>
        <InputField
          fieldClassName="col-xs-12"
          labelClassName="control-label col-xs-12"
          placeholder="Назначение"
          labelText="Назначение"
          name="purpose"
        />
      </Grid>
      {userData && (
        <Grid item xs={12} md={6} xl={4}>
          <InputField
            fieldClassName="col-xs-12"
            labelClassName="control-label col-xs-12"
            placeholder="Форма"
            labelText="Форма"
            name="form"
          />
        </Grid>
      )}
      <Grid item xs={12} md={6} xl={4}>
        <InputField
          fieldClassName="col-xs-12"
          labelClassName="control-label col-xs-12"
          placeholder="Состав"
          labelText="Состав"
          name="composition"
        />
      </Grid>
      <Grid item xs={12} md={6} xl={4}>
        <SelectSearchField
          clearable
          fieldClassName="col-xs-12 selectpicker"
          labelClassName="col-xs-12 control-label"
          labelKey="label"
          valueKey="value"
          labelText="Содержит ГМО"
          name="presenceGMO"
          placeholder=""
          options={yesNoOptions}
        />
      </Grid>
      {userData && (
        <Grid item xs={12} md={6} xl={4}>
          <SelectSearchField
            multiple
            clearable
            fieldClassName="col-xs-12 selectpicker"
            labelClassName="col-xs-12 control-label"
            labelKey="view"
            valueKey="code"
            labelText="Тип"
            name="feedAdditiveTypeCodeList"
            placeholder=""
            options={additiveTypesOptions}
          />
        </Grid>
      )}
      <Grid item xs={12} md={6} xl={4}>
        <InputField
          fieldClassName="col-xs-12"
          labelClassName="control-label col-xs-12"
          labelText="Последние четыре цифры учетной серии"
          placeholder="Последние четыре цифры учетной серии"
          name="series"
        />
      </Grid>
      <Grid item xs={12} md={6} xl={4}>
        <InputField
          fieldClassName="col-xs-12"
          labelClassName="control-label col-xs-12"
          labelText="Последние пять цифр регистрационного номера"
          placeholder="Последние пять цифр регистрационного номера"
          name="regNumber"
        />
      </Grid>
      <Grid item xs={12} md={6} xl={4}>
        <SelectSearchField
          clearable
          fieldClassName="col-xs-12 selectpicker"
          labelClassName="col-xs-12 control-label"
          labelKey="name"
          valueKey="code"
          labelText="Статус"
          name="registryStatus"
          placeholder=""
          options={registryStatusOptions}
        />
      </Grid>
      <Grid item xs={12} md={6} xl={4}>
        <DateRangeField
          fieldClassName="col-xs-12"
          labelClassName="col-xs-12 control-label"
          labelText="Дата регистрации КД"
          name="startValidityDate"
        />
      </Grid>
      {userData && (
        <>
          <Grid item xs={12} md={6} xl={4}>
            <DateRangeField
              fieldClassName="col-xs-12"
              labelClassName="col-xs-12 control-label"
              labelText="Дата регистрации заявки"
              name="requestDate"
            />
          </Grid>
          <Grid item xs={12} md={6} xl={4}>
            <SelectSearchField
              multiple
              clearable
              fieldClassName="col-xs-12 selectpicker"
              labelClassName="col-xs-12 control-label"
              labelKey="view"
              valueKey="code"
              labelText="Вид"
              name="feedAdditiveKindCodeList"
              placeholder=""
              options={additiveKindOptions}
            />
          </Grid>
          <Grid item xs={12} md={6} xl={4}>
            <SelectSearchField
              multiple
              clearable
              fieldClassName="col-xs-12 selectpicker"
              labelClassName="col-xs-12 control-label"
              labelKey="name"
              valueKey="id"
              labelText="Вид животного"
              name="animalSpeciesList"
              placeholder=""
              options={animalTypesOptions}
            />
          </Grid>
          <Grid item xs={12} md={6} xl={4}>
            <InputField
              fieldClassName="col-xs-12"
              labelClassName="control-label col-xs-12"
              labelText="Регистрационный номер ЕРУЛ"
              placeholder="Регистрационный номер ЕРУЛ"
              name="erulRegNumber"
            />
          </Grid>
          {hasCerberusPermission && (
            <Grid item xs={12} md={6} xl={4}>
              <SelectSearchField
                clearable
                fieldClassName="col-xs-12 selectpicker"
                labelClassName="col-xs-12 control-label"
                labelKey="label"
                valueKey="value"
                placeholder=""
                labelText="Данные о ХС"
                name="businessEntitySearchCriteria"
                options={cerberusIntegrationStatusOptions}
              />
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
};
